import React, { useEffect, useRef } from "react";

const WeeklyDetailsTable = ({
  weekData,
  formatCurrency,
  monthlyAverage,
  onLoadMore,
  hasMore,
  onEntryClick,
  onAddPosition,
  filters,
  onFilterChange,
  onStatusChange,
}) => {
  const tableEndRef = useRef(null);

  useEffect(() => {
    if (!tableEndRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && onLoadMore) {
          console.log("📊 Lade weitere Wochen...");
          onLoadMore();
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(tableEndRef.current);

    return () => {
      if (tableEndRef.current) {
        observer.unobserve(tableEndRef.current);
      }
    };
  }, [hasMore, onLoadMore]);

  if (!weekData || weekData.length === 0) {
    return null;
  }

  return (
    <div className="weekly-details-table-container">
      <div className="weekly-details-header">
        <div className="weekly-details-title-row">
          <h3>Detaillierte Wochenübersicht</h3>
          {onAddPosition && (
            <button
              onClick={onAddPosition}
              className="btn btn-primary btn-sm"
              style={{ marginLeft: "auto" }}
            >
              + Neue Position
            </button>
          )}
        </div>
        {monthlyAverage > 0 && (
          <p className="monthly-average">
            Ø Monatliche Ausgaben: {formatCurrency(monthlyAverage)}
          </p>
        )}

        {/* Filterfunktion */}
        {filters && onFilterChange && (
          <div className="weekly-details-filters">
            <div className="filter-row">
              <div className="filter-group">
                <label>Typ: </label>
                <select
                  value={filters.typ || ""}
                  onChange={(e) => onFilterChange("typ", e.target.value)}
                  className="select-small"
                >
                  <option value="">Alle</option>
                  <option value="Einnahme">Einnahme</option>
                  <option value="Ausgabe">Ausgabe</option>
                </select>
              </div>

              <div className="filter-group">
                <label>Name: </label>
                <input
                  type="text"
                  value={filters.name || ""}
                  onChange={(e) => onFilterChange("name", e.target.value)}
                  placeholder="Suche..."
                  className="select-small"
                />
              </div>

              <div className="filter-group">
                <label>Status: </label>
                <select
                  value={filters.status || ""}
                  onChange={(e) => onFilterChange("status", e.target.value)}
                  className="select-small"
                >
                  <option value="">Alle</option>
                  <optgroup label="Einnahmen">
                    <option value="expected">Erwartet</option>
                    <option value="sent">Versendet</option>
                    <option value="delayed">Verzögert</option>
                    <option value="overdue">Überfällig</option>
                  </optgroup>
                  <optgroup label="Ausgaben">
                    <option value="expecting">Erwartet</option>
                    <option value="received">Erhalten</option>
                    <option value="entered_payment">Zahlung eingegeben</option>
                  </optgroup>
                  <option value="paid">Bezahlt</option>
                </select>
              </div>

              <div className="filter-actions">
                {(filters.typ || filters.name || filters.status) && (
                  <button
                    onClick={() => {
                      onFilterChange("typ", "");
                      onFilterChange("name", "");
                      onFilterChange("status", "");
                    }}
                    className="btn btn-secondary btn-sm"
                  >
                    Filter zurücksetzen
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="table-wrapper">
        <table className="weekly-details-table">
          <thead>
            <tr>
              <th className="week-col">Woche</th>
              <th className="details-col">Einnahmen</th>
              <th className="amount-col">Summe</th>
              <th className="details-col">Ausgaben</th>
              <th className="amount-col">Summe</th>
              <th className="cashflow-col">Cashflow</th>
            </tr>
          </thead>
          <tbody>
            {weekData.map((week, index) => {
              const einnahmenSum = week.einnahmen + (week.einnahmenOverdue || 0) + week.einnahmenPrognose;
              const ausgabenSum = week.ausgaben + week.ausgabenPrognose;
              const netto = einnahmenSum - ausgabenSum;

              // Helper function to get status badge config
              const getStatusConfig = (entry) => {
                if (!entry.computedStatus) return null;

                const statusConfig = {
                  // Einnahmen-Status
                  expected: { label: "Erwartet", title: "Erwartet", className: "status-expected" },
                  sent: { label: "Versendet", title: "Versendet", className: "status-sent" },
                  delayed: { label: "Verzögert", title: "Verzögert", className: "status-delayed" },
                  overdue: { label: "Überfällig", title: "Überfällig", className: "status-overdue" },
                  paid: { label: "Bezahlt", title: "Bezahlt", className: "status-paid" },
                  // Ausgaben-Status
                  expecting: { label: "Erwartet", title: "Rechnung wird erwartet", className: "status-expected" },
                  received: { label: "Erhalten", title: "Rechnung erhalten", className: "status-sent" },
                  entered_payment: { label: "Zahlung eingegeben", title: "Zahlung wurde eingegeben", className: "status-sent" },
                };

                return statusConfig[entry.computedStatus] || null;
              };

              // Helper function to get next status action
              const getNextStatusAction = (entry) => {
                if (!entry.computedStatus || !entry.id || entry.ist_mwst_abrechnung) return null;

                // Keine Status-Buttons für automatische Zahlungen
                if (entry.automatic_payment === true) return null;

                // Logik für Einnahmen
                if (entry.typ === "Einnahme") {
                  if (entry.computedStatus === "expected" || entry.computedStatus === "delayed") {
                    return { action: "sent", label: "Versendet", title: "Als versendet markieren" };
                  } else if (entry.computedStatus === "sent" || entry.computedStatus === "overdue") {
                    return { action: "paid", label: "Bezahlt", title: "Als bezahlt markieren" };
                  }
                }

                // Logik für Ausgaben
                if (entry.typ === "Ausgabe") {
                  if (entry.computedStatus === "expecting") {
                    return { action: "received", label: "Erhalten", title: "Als erhalten markieren" };
                  } else if (entry.computedStatus === "received") {
                    return { action: "paid", label: "Bezahlt", title: "Als bezahlt markieren" };
                  } else if (entry.computedStatus === "entered_payment") {
                    return { action: "paid", label: "Bezahlt", title: "Als bezahlt markieren" };
                  }
                }

                return null;
              };

              // Apply filters
              let einnahmenList = [
                ...week.budgetDetails.filter((d) => d.typ === "Einnahme"),
                ...week.mwstDetails.filter((d) => d.typ === "Einnahme"),
              ];

              let ausgabenList = [
                ...week.budgetDetails.filter((d) => d.typ === "Ausgabe"),
                ...week.mwstDetails.filter((d) => d.typ === "Ausgabe"),
              ];

              // Apply filters if provided
              if (filters) {
                if (filters.typ) {
                  einnahmenList = filters.typ === "Einnahme" ? einnahmenList : [];
                  ausgabenList = filters.typ === "Ausgabe" ? ausgabenList : [];
                }
                if (filters.name) {
                  const searchTerm = filters.name.toLowerCase();
                  einnahmenList = einnahmenList.filter(e =>
                    e.name?.toLowerCase().includes(searchTerm)
                  );
                  ausgabenList = ausgabenList.filter(e =>
                    e.name?.toLowerCase().includes(searchTerm) ||
                    e.displayName?.toLowerCase().includes(searchTerm)
                  );
                }
                if (filters.status) {
                  einnahmenList = einnahmenList.filter(e =>
                    e.computedStatus === filters.status
                  );
                  ausgabenList = ausgabenList.filter(e =>
                    e.computedStatus === filters.status
                  );
                }
              }

              // Sortiere nach Betrag
              einnahmenList.sort((a, b) => b.betrag - a.betrag);
              ausgabenList.sort((a, b) => b.betrag - a.betrag);

              return (
                <tr key={index} className="week-row" data-week-index={week.weekIndex}>
                  <td className="week-label">
                    <div className="week-number">{week.shortLabel}</div>
                    <div className="week-dates">
                      {week.weekStart.getDate()}.{week.weekStart.getMonth() + 1}
                    </div>
                  </td>

                  <td className="details-cell">
                    {einnahmenList.length > 0 ? (
                      <div className="entry-list">
                        {einnahmenList.map((entry, idx) => {
                          const statusConfig = getStatusConfig(entry);
                          const nextAction = getNextStatusAction(entry);

                          return (
                            <div
                              key={idx}
                              className={`entry-item ${
                                entry.ist_prognose ? "prognose" : ""
                              } ${entry.id && !entry.ist_mwst_abrechnung ? "clickable" : ""}`}
                            >
                              {/* Status Icon */}
                              {statusConfig && (
                                <span
                                  className={`entry-status-icon ${statusConfig.className}`}
                                  title={statusConfig.title}
                                >
                                  {statusConfig.label}
                                </span>
                              )}

                              {/* Entry Name und Details */}
                              <span
                                className="entry-name"
                                onClick={() => {
                                  if (entry.id && !entry.ist_mwst_abrechnung && onEntryClick) {
                                    onEntryClick(entry);
                                  }
                                }}
                                style={{ cursor: entry.id && !entry.ist_mwst_abrechnung ? "pointer" : "default" }}
                              >
                                {entry.name}
                                {entry.timing && entry.timing !== "Einmalig" && (
                                  <span className="entry-badge">
                                    {entry.timing.split(" ")[0]}
                                  </span>
                                )}
                                {entry.ist_prognose && (
                                  <span className="entry-badge prognose">
                                    Prognose
                                  </span>
                                )}
                              </span>

                              <span className="entry-amount positive">
                                {formatCurrency(entry.betrag)}
                                {entry.eigenanteil != null && entry.eigenanteilRatio < 1 && (
                                  <span className="eigenanteil-badge" title={`Eigenanteil: ${Math.round(entry.eigenanteilRatio * 100)}%`}>
                                    ({formatCurrency(entry.eigenanteil)})
                                  </span>
                                )}
                              </span>

                              {/* Status Change Button */}
                              {nextAction && onStatusChange && (
                                <button
                                  className="entry-status-button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onStatusChange(entry.id, nextAction.action);
                                  }}
                                  title={nextAction.title}
                                >
                                  {nextAction.label}
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <span className="empty-text">—</span>
                    )}
                  </td>

                  <td className="amount-total positive">
                    {einnahmenSum > 0 ? (
                      <>
                        {formatCurrency(week.einnahmen + (week.einnahmenOverdue || 0))}
                        {week.einnahmenPrognose > 0 && (
                          <span className="prognose-sum">+ {formatCurrency(week.einnahmenPrognose)}</span>
                        )}
                        {(() => {
                          const eigenanteilSum = einnahmenList.reduce((s, e) => s + (e.eigenanteil || e.betrag), 0);
                          return eigenanteilSum < einnahmenSum ? (
                            <span className="eigenanteil-sum">({formatCurrency(eigenanteilSum)})</span>
                          ) : null;
                        })()}
                      </>
                    ) : "—"}
                  </td>

                  <td className="details-cell">
                    {ausgabenList.length > 0 ? (
                      <div className="entry-list">
                        {ausgabenList.map((entry, idx) => {
                          const statusConfig = getStatusConfig(entry);
                          const nextAction = getNextStatusAction(entry);

                          return (
                            <div
                              key={idx}
                              className={`entry-item ${
                                entry.ist_prognose ? "prognose" : ""
                              } ${entry.id && !entry.ist_mwst_abrechnung ? "clickable" : ""}`}
                            >
                              {/* Status Icon */}
                              {statusConfig && (
                                <span
                                  className={`entry-status-icon ${statusConfig.className}`}
                                  title={statusConfig.title}
                                >
                                  {statusConfig.label}
                                </span>
                              )}

                              {/* Entry Name und Details — Projekt-Ausgaben (Subplaner:in)
                                  als {Projektcode} - {Phase} - {Firma}; volle Bezeichnung
                                  bei Überlänge via Tooltip */}
                              <span
                                className="entry-name"
                                onClick={() => {
                                  if (entry.id && !entry.ist_mwst_abrechnung && onEntryClick) {
                                    onEntryClick(entry);
                                  }
                                }}
                                style={{ cursor: entry.id && !entry.ist_mwst_abrechnung ? "pointer" : "default" }}
                              >
                                <span
                                  className="entry-name-text"
                                  title={entry.displayName || entry.name}
                                >
                                  {entry.displayName || entry.name}
                                </span>
                                {entry.timing && entry.timing !== "Einmalig" && (
                                  <span className="entry-badge">
                                    {entry.timing.split(" ")[0]}
                                  </span>
                                )}
                                {entry.ist_prognose && (
                                  <span className="entry-badge prognose">
                                    Prognose
                                  </span>
                                )}
                              </span>

                              <span className="entry-amount negative">
                                {formatCurrency(entry.betrag)}
                              </span>

                              {/* Status Change Button */}
                              {nextAction && onStatusChange && (
                                <button
                                  className="entry-status-button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onStatusChange(entry.id, nextAction.action);
                                  }}
                                  title={nextAction.title}
                                >
                                  {nextAction.label}
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <span className="empty-text">—</span>
                    )}
                  </td>

                  <td className="amount-total negative">
                    {ausgabenSum > 0 ? (
                      <>
                        {formatCurrency(week.ausgaben)}
                        {week.ausgabenPrognose > 0 && (
                          <span className="prognose-sum">+ {formatCurrency(week.ausgabenPrognose)}</span>
                        )}
                      </>
                    ) : "—"}
                  </td>

                  <td
                    className={`cashflow-value ${
                      netto >= 0 ? "positive" : "negative"
                    }`}
                  >
                    <div className="cashflow-amount">
                      {formatCurrency(week.kumulativ)}
                    </div>
                    {week.kumulativEigenanteil != null && week.kumulativEigenanteil !== week.kumulativ && (
                      <div
                        className="cashflow-eigenanteil"
                        title={`Eigenanteil: Saldo abzüglich ${formatCurrency(
                          week.subplanerSchuld || 0
                        )} anteilig verdienter, noch nicht bezahlter Projekt-Honorare`}
                      >
                        ({formatCurrency(week.kumulativEigenanteil)})
                      </div>
                    )}
                    <div className="cashflow-change">
                      {netto >= 0 ? "+" : ""}
                      {formatCurrency(netto)}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div ref={tableEndRef} style={{ height: "20px" }} />
      </div>
    </div>
  );
};

export default WeeklyDetailsTable;
